import React from "react";
import { UsersIcon, GlobeAltIcon, HeartIcon, ShieldCheckIcon, SparklesIcon } from "@heroicons/react/24/outline";
import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";
import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";
import { Helmet } from "react-helmet-async";

export default function AboutUs() {
  return (

    <>
      <Helmet>
        <title>
          About Us | Dubai Cheap Hotels
        </title>

        <meta
          name="description"
          content="Learn more about Dubai Cheap Hotels, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <link
          rel="canonical"
          href="http://127.0.0.1:8000/about-us"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="About Us | Dubai Cheap Hotels"
        />

        <meta
          property="og:description"
          content="Learn more about Dubai Cheap Hotels, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <meta
          property="og:url"
          content="http://127.0.0.1:8000/about-us"
        />

        <meta
          property="og:type"
          content="website"
        />

        {/* Twitter */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About Us | Dubai Cheap Hotels"
        />

        <meta
          name="twitter:description"
          content="Learn more about Dubai Cheap Hotels, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>


      <div className="w-full bg-gray-50">
        <Hero
          variant="inner"
          title="About Us"
          subtitle="We help travelers discover the perfect stay with ease, comfort, and confidence."
          backgroundImage={heroImg}
        />

        {/* Our Story - Modern Split */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="relative group">
            <img
              src={aboutImg}
              alt="about"
              className="rounded-3xl shadow-2xl w-full h-[250px] sm:h-[320px] md:h-[400px] object-cover group-hover:scale-105 transition duration-500"
            />

            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-white p-4 sm:p-5 rounded-2xl shadow-xl w-40 sm:w-48">
              <p className="text-sm text-gray-500">Years Experience</p>
              <h3 className="text-2xl font-bold text-[#FF6B35]">25+</h3>
            </div>
          </div>

          <div>
            <span className="text-[#FF6B35] font-semibold uppercase tracking-wide">Our Story</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-2 mb-4 leading-tight">
              Making Travel Simple & Enjoyable
            </h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              We started with a simple mission: make hotel booking easy, fast, and reliable. Whether you're traveling for business or leisure, we aim to provide the best options tailored to your needs.
            </p>
            <p className="text-gray-600 leading-relaxed">
              With a growing network of trusted hotels and partners, we ensure every stay is comfortable, secure, and memorable.
            </p>

            <div className="mt-6 flex flex-wrap gap-3 sm:gap-4">
              <div className="bg-white shadow-md px-4 py-3 rounded-xl">
                <h4 className="font-bold text-[#FF6B35]">500+</h4>
                <p className="text-xs text-gray-500">Hotels</p>
              </div>
              <div className="bg-white shadow-md px-4 py-3 rounded-xl">
                <h4 className="font-bold text-[#FF6B35]">10K+</h4>
                <p className="text-xs text-gray-500">Happy Clients</p>
              </div>
              <div className="bg-white shadow-md px-4 py-3 rounded-xl">
                <h4 className="font-bold text-[#FF6B35]">24/7</h4>
                <p className="text-xs text-gray-500">Support</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission / Vision - Gradient Cards */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 px-4 sm:px-0">
            <div class="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white p-8 rounded-3xl shadow-xl hover:scale-[1.02] transition">
              <SparklesIcon className="w-10 h-10 mb-4" />
              <h3 className="!text-white text-2xl font-bold mb-3">Our Mission</h3>
              <p className="opacity-90">
                To simplify travel by connecting people with the best hotels at the best prices.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-xl hover:scale-[1.02] transition">
              <SparklesIcon className="w-10 h-10 mb-4 text-[#FF6B35]" />
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-gray-600">
                To become a globally trusted platform for hotel booking and travel experiences.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us - Modern Cards */}
        <section className="bg-gray-100 py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12">Why Choose Us</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[{
                icon: UsersIcon,
                title: "Trusted by Users",
                desc: "Thousands of happy customers worldwide"
              }, {
                icon: GlobeAltIcon,
                title: "Global Destinations",
                desc: "Explore hotels across top locations"
              }, {
                icon: HeartIcon,
                title: "Best Experience",
                desc: "We focus on comfort and satisfaction"
              }, {
                icon: ShieldCheckIcon,
                title: "Secure Booking",
                desc: "Safe and reliable transactions"
              }].map((item, i) => (
                <div key={i} className="group bg-white p-8 rounded-3xl shadow-md hover:shadow-2xl transition relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-500 opacity-0 group-hover:opacity-10 transition"></div>

                  <item.icon className="w-12 h-12 mx-auto text-[#FF6B35] mb-4 group-hover:scale-110 transition" />
                  <h4 className="font-semibold mb-2 text-lg">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA - Enhanced */}
        <CTASection />
      </div>
    </>
  );
}
